import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { recurrenceTypes } from '../../../../settings/constants';
import { RecurrencePattern, RecurrencePatternDefaultValues } from '../../../../propTypes/entities/recurrencePattern';
import globals from '../../../../globals/communityGlobals';
import { numOfDaysInA, numberOf } from '../../../../settings/other';
import { getDateForApi, durationBetweenDates } from '../../../../utils/dates';
import { actionTypes } from '../../Preview/TreatmentPreview';

const locale = globals.getArea('treatments');

const defaultRecurrenceOptions = [
  { value: recurrenceTypes.DISABLED, name: locale.getMsg('none') },
  { value: recurrenceTypes.DAILY, name: locale.getMsg('daily') },
  { value: recurrenceTypes.WEEKLY, name: locale.getMsg('weekly') },
  { value: recurrenceTypes.MONTHLY, name: locale.getMsg('monthly') },
];

class SharedForm extends React.Component {
  constructor(props) {
    super(props);
    const { dateFrom, dateTo } = props.values;
    const recurrenceType = this.getRecurrenceTypeOptions(props.values);
    const recurEvery = this.getRecurEveryOptions(props.values);
    const monthlyRecurrenceType = this.getMonthlyRecurrenceType(props.values);
    const selectedWeeks = this.getSelectedWeeks(props.values);
    const selectedWeekDays = this.getSelectedWeekDays(props.values);
    const selectedDays = this.getSelectedDays(props.values);
    const duration = this.calculateDuration(props.values);

    this.state = {
      recurrenceType,
      recurEvery,
      dateFrom,
      dateTo,
      monthlyRecurrenceType,
      selectedWeeks,
      selectedWeekDays,
      selectedDays,
      duration,
    };
  }
  getMonthlyRecurrenceType = values => values.monthlyRecurrenceType;

  getSelectedWeeks = values => values.selectedWeeks;

  getSelectedWeekDays = values => values.selectedWeekDays;

  getSelectedDays = values => values.selectedDays;

  getRecurEveryOptions = values => {
    // values === the values received from the form's onChange event
    const { dateFrom, dateTo, recurrenceType, recurEvery } = values;
    const recurEveryOptions = [];

    // if there's no recurrence pattern skip everything and return an empty field
    if (recurrenceType === recurrenceTypes.DISABLED) {
      return {
        options: [],
        disabled: true,
        selected: undefined,
      };
    }

    let disabled = false;
    // Set the default highest number to the number of days in a month
    let highestNumber = numberOf.daysInAMonth;

    if (recurrenceType === recurrenceTypes.DAILY) {
      highestNumber = numberOf.daysInAMonth;
    }

    if (recurrenceType === recurrenceTypes.WEEKLY) {
      highestNumber = numberOf.weeksInAYear;
    }

    if (recurrenceType === recurrenceTypes.MONTHLY) {
      highestNumber = numberOf.monthsInAYear;
    }

    if (!dateFrom) {
      disabled = true;
    } else if (dateFrom && dateTo) {
      const differenceInDays = moment(moment(dateTo).startOf('day')).diff(moment(dateFrom).startOf('day'), 'days');
      switch (recurrenceType) {
        default:
        case recurrenceTypes.DAILY:
          // If recurrenceType is set to daily,
          // the highest number is either the difference in days or the number of days in a month
          highestNumber = differenceInDays > numberOf.daysInAMonth ? numberOf.daysInAMonth : differenceInDays;
          break;
        case recurrenceTypes.WEEKLY:
          // If recurrenceType is set to either weekly ('2') or monthly ('3'),
          // divide it by the number of days in the given period and floor the result
          highestNumber = Math.floor(differenceInDays / numberOf.weeksInAYear);
          break;
        case recurrenceTypes.MONTHLY:
          highestNumber = Math.floor(differenceInDays / numberOf.monthsInAYear);
          break;
      }
    }
    // Generate the options
    for (let i = 1; i <= highestNumber; i += 1) {
      recurEveryOptions.push({ value: i });
    }
    // If there are no options to choose, disable the dropdown
    if (recurEveryOptions.length < 1) {
      disabled = true;
    }
    // If the previously selected option is missing from new options or the dropdown is disabled,
    // set it to the default value
    let selected = this.props.defaultValues.recurEvery;

    if (recurEveryOptions.findIndex(el => el.value === recurEvery) >= 0) {
      selected = recurEvery;
    } else {
      selected = 1;
    }

    if (disabled) {
      selected = recurrenceTypes.DISABLED;
    }

    return {
      disabled,
      selected,
      options: recurEveryOptions,
    };
  };
  getRecurrenceTypeOptions = values => {
    // values === the values received from the form's onChange event
    const { dateFrom, dateTo, recurrenceType } = values;

    let recurrenceOptions = defaultRecurrenceOptions;
    let disabled = false;
    let defaultRecurrenceType = this.props.defaultValues.recurrenceType;

    if (!dateFrom) {
      // Disable the dropdown if dateFrom is not set
      disabled = true;
    } else if (dateFrom && dateTo) {
      const differenceInDays = moment(moment(dateTo).startOf('day')).diff(moment(dateFrom).startOf('day'), 'days');
      if (differenceInDays <= 0) {
        // If the difference is 0 (or lower if something goes wrong), disable the dropdown
        disabled = true;
        recurrenceOptions = defaultRecurrenceOptions.slice(0, 1);
        defaultRecurrenceType = recurrenceTypes.DISABLED;
      } else if (differenceInDays > 0 && differenceInDays < numOfDaysInA.week - 1) {
        // If it's between 1 and 6, show Daily
        recurrenceOptions = defaultRecurrenceOptions.slice(1, 2);
        defaultRecurrenceType = recurrenceTypes.DAILY;
      } else if (differenceInDays >= numOfDaysInA.week - 1 && differenceInDays < numOfDaysInA.month - 1) {
        // If it's between 7 and 27, show Daily and Weekly
        recurrenceOptions = defaultRecurrenceOptions.slice(1, 3);
        defaultRecurrenceType = recurrenceTypes.DAILY;
      } else if (differenceInDays >= numOfDaysInA.month - 1) {
        // If it's higher show all of the options
        recurrenceOptions = defaultRecurrenceOptions.slice(1, 4);
        defaultRecurrenceType = recurrenceTypes.DAILY;
      }
    } else if (!dateTo) {
      recurrenceOptions = defaultRecurrenceOptions.slice(1, 4);
      defaultRecurrenceType = recurrenceTypes.DAILY;
    }
    // If the previously selected option is missing from new options,
    // set it to the default value
    let selected = defaultRecurrenceType;
    if (recurrenceOptions.findIndex(el => el.value === recurrenceType) >= 0) {
      selected = recurrenceType;
    }
    if (disabled) {
      selected = recurrenceTypes.DISABLED;
    }

    return {
      disabled,
      selected,
      options: recurrenceOptions,
    };
  };

  getSelectionMatrixForReviewPicker = () => {
    if (this.form) {
      const formModel = this.form.getModel();
      return this.isSpecific ? formModel.selectedDays : formModel.timeSlotsMatrix;
    }
    return undefined;
  };

  calculateDuration = values => {
    if (values.endTime && values.startTime) {
      const value = durationBetweenDates(new Date(values.startTime), new Date(values.endTime));
      return value >= 0 && value < 1440 ? value : 0;
    }
    return undefined;
  };

  isAtLeastTodayOrUnmodified = fieldName => (values, value) => {
    const { defaultValues, actionType } = this.props;
    if (
      typeof value === 'undefined' ||
      moment(value).isSame(moment(defaultValues[fieldName]), 'day') ||
      (fieldName === 'dateFrom' && actionType === actionTypes.create)
    ) {
      return true;
    }
    return moment(value).isSameOrAfter(globals.now, 'day');
  };
  createPatternForReviewPicker = () => {
    const {
      recurrenceType,
      recurEvery,
      dateFrom,
      dateTo,
      monthlyRecurrenceType,
      selectedWeeks,
      selectedWeekDays,
      selectedDays,
    } = this.state;
    if (!recurrenceType || !recurEvery) return null;
    return {
      recurrenceType: recurrenceType.selected,
      recurEvery: recurEvery.selected === recurrenceTypes.DISABLED || !recurEvery.selected ? 1 : recurEvery.selected,
      isSpecific: this.isSpecific,
      dateFrom: getDateForApi(dateFrom),
      dateTo: getDateForApi(dateTo),
      monthlyRecurrenceType,
      selectedWeeks,
      selectedWeekDays,
      selectedDays:
        !this.isSpecific && recurrenceType.selected === recurrenceTypes.MONTHLY && selectedDays
          ? Object.keys(selectedDays)
          : null, // specific time uses SpecificSelectedDays prop from the query
    };
  };
  handleChange = values => {
    const { dateFrom, dateTo } = values;
    const recurrenceType = this.getRecurrenceTypeOptions(values);
    const recurEvery = this.getRecurEveryOptions(values);
    const duration = this.calculateDuration(values);
    const monthlyRecurrenceType = this.getMonthlyRecurrenceType(values);
    const selectedWeeks = this.getSelectedWeeks(values);
    const selectedDays = this.getSelectedDays(values);
    const selectedWeekDays = this.getSelectedWeekDays(values);

    this.props.onChange({ ...values });
    this.setState({
      dateFrom,
      dateTo,
      recurrenceType,
      recurEvery,
      duration,
      monthlyRecurrenceType,
      selectedWeeks,
      selectedDays,
      selectedWeekDays,
    });
  };
  handleButtonClicks = (row, position) => {
    const { getValue, setValue } = this.props;
    const stateOfButtons = getValue() || {};
    const currentButtonRowState = stateOfButtons[row] || [];

    if (stateOfButtons[row] && stateOfButtons[row][position]) {
      delete stateOfButtons[row][position];
      if (Object.keys(stateOfButtons[row]).length <= 0) {
        delete stateOfButtons[row];
        if (Object.keys(stateOfButtons).length <= 0) {
          // If nothing is selected, set the value to undefined (default in formsy)
          return setValue(undefined);
        }
      }
      return setValue({
        ...stateOfButtons,
      });
    }
    return setValue({
      ...stateOfButtons,
      [row]: {
        ...currentButtonRowState,
        [position]: true,
      },
    });
  };
}

SharedForm.defaultProps = {
  defaultValues: RecurrencePatternDefaultValues,
  values: RecurrencePatternDefaultValues,
};

SharedForm.propTypes = {
  defaultValues: RecurrencePattern,
  values: RecurrencePattern,
  getValue: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default SharedForm;
